/**
 * Universal Clipboard Types
 *
 * The clipboard is the primary ingestion primitive for AOS.
 * All content enters through paste operations.
 */

import type { DAGObject, ContentTypes } from '../dag/types.js';

// ============================================================================
// Content Types
// ============================================================================

/**
 * MIME type categories for clipboard content
 */
export const CLIPBOARD_MIME_TYPES = {
  // AOS-specific types
  BOOT_IMAGE: 'application/aos-boot',
  PACKAGE: 'application/aos-package',
  INVITE: 'application/aos-invite',
  GRANT: 'application/aos-grant',

  // Text types
  TEXT_PLAIN: 'text/plain',
  TEXT_HTML: 'text/html',
  TEXT_MARKDOWN: 'text/markdown',

  // Code types (prefix)
  CODE_PREFIX: 'text/x-',

  // Media types
  IMAGE_PNG: 'image/png',
  IMAGE_JPEG: 'image/jpeg',
  IMAGE_GIF: 'image/gif',
  IMAGE_WEBP: 'image/webp',
  IMAGE_SVG: 'image/svg+xml',
  VIDEO_WEBM: 'video/webm',
  VIDEO_MP4: 'video/mp4',
  AUDIO_WEBM: 'audio/webm',
  AUDIO_MP3: 'audio/mpeg',

  // Document types
  PDF: 'application/pdf',
  JSON: 'application/json',

  // Fallback
  OCTET_STREAM: 'application/octet-stream',
} as const;

export type ClipboardMimeType = typeof CLIPBOARD_MIME_TYPES[keyof typeof CLIPBOARD_MIME_TYPES] | string;

// ============================================================================
// Export Policies
// ============================================================================

/**
 * Export permission levels
 */
export type ExportPolicy =
  | 'owner'     // Only original uploader can export
  | 'granted'   // Uploader + explicit grantees
  | 'admin'     // Admins (logged, audited)
  | 'public';   // Anyone in universe

/**
 * View permission levels
 */
export type ViewPolicy =
  | 'private'   // Only owner
  | 'team'      // Team members
  | 'universe'  // Anyone in universe
  | 'public';   // Anyone

// ============================================================================
// Clipboard Entry
// ============================================================================

/**
 * Device provenance information
 */
export interface DeviceProvenance {
  /** Device Ed25519 public key (base58) */
  deviceId: string;
  /** Human-readable device alias */
  deviceAlias: string;
  /** Browser user agent (if applicable) */
  browserUA?: string;
  /** Peer ID if different from device ID */
  peerId?: string;
}

/**
 * User provenance information
 */
export interface UserProvenance {
  /** User Ed25519 public key (base58) */
  userId: string;
  /** User alias/display name */
  alias?: string;
  /** Invite chain depth (how many hops from universe creator) */
  inviteChainDepth?: number;
}

/**
 * Content metadata
 */
export interface ContentMeta {
  /** MIME type */
  type: ClipboardMimeType;
  /** DAG hash of chunked content */
  dag: string;
  /** Total size in bytes */
  size: number;
  /** Number of chunks */
  chunks: number;
  /** SHA-256 of original content (for verification) */
  sha256?: string;
  /** Thumbnail DAG hash (for media) */
  thumbnailDag?: string;
}

/**
 * User-provided metadata
 */
export interface EntryMeta {
  /** User-provided label */
  label?: string;
  /** Tags (comma-separated or array) */
  tags?: string[];
  /** Source URL (if pasted from web) */
  sourceUrl?: string;
  /** Original filename (if pasted from file) */
  filename?: string;
}

/**
 * Permission configuration
 */
export interface EntryPermissions {
  /** Export policy */
  export: ExportPolicy;
  /** View policy */
  view: ViewPolicy;
  /** Active export grants (DAG hashes) */
  grants: string[];
}

/**
 * Clipboard entry content (stored in DAG)
 */
export interface ClipboardEntryContent {
  /** Entry version */
  version: 1;
  /** Unique entry ID */
  id: string;
  /** ISO timestamp */
  date: string;
  /** Device provenance */
  from: DeviceProvenance;
  /** User provenance */
  by: UserProvenance;
  /** Content metadata */
  content: ContentMeta;
  /** User metadata */
  meta: EntryMeta;
  /** Permissions */
  permissions: EntryPermissions;
}

/**
 * Full clipboard entry (DAG object)
 */
export type ClipboardEntry = DAGObject<ClipboardEntryContent>;

// ============================================================================
// Content Chunks
// ============================================================================

/**
 * Default chunk size (256KB)
 */
export const DEFAULT_CHUNK_SIZE = 256 * 1024;

/**
 * Chunk content (stored in DAG)
 */
export interface ChunkContent {
  /** Chunk index */
  index: number;
  /** Total chunks */
  total: number;
  /** Parent manifest hash */
  manifest: string;
  /** Chunk data (base64 encoded) */
  data: string;
}

/**
 * Chunk manifest (stored in DAG)
 */
export interface ChunkManifestContent {
  /** Original content size */
  size: number;
  /** SHA-256 of original content */
  sha256: string;
  /** Chunk hashes in order */
  chunks: string[];
  /** MIME type */
  mimeType: string;
}

/**
 * Full chunk (DAG object)
 */
export type Chunk = DAGObject<ChunkContent>;

/**
 * Full chunk manifest (DAG object)
 */
export type ChunkManifest = DAGObject<ChunkManifestContent>;

// ============================================================================
// Export Grant
// ============================================================================

/**
 * Export grant content
 */
export interface ExportGrantContent {
  /** Grant version */
  version: 1;
  /** Action type */
  action: 'export';
  /** DAG hash of content being granted */
  contentDag: string;
  /** Granter's public key */
  from: string;
  /** Recipient's public key, or '*' for anyone */
  to: string;
  /** Unix timestamp when grant expires */
  expires?: number;
  /** Maximum number of exports allowed */
  maxUses?: number;
  /** Current use count */
  useCount?: number;
}

/**
 * Full export grant (DAG object)
 */
export type ExportGrant = DAGObject<ExportGrantContent>;

// ============================================================================
// Admin Export Log
// ============================================================================

/**
 * Admin export log entry content
 */
export interface AdminExportLogContent {
  /** Admin's public key */
  admin: string;
  /** DAG hash of exported content */
  contentDag: string;
  /** Reason for admin export */
  reason: string;
}

/**
 * Full admin export log (DAG object)
 */
export type AdminExportLog = DAGObject<AdminExportLogContent>;

// ============================================================================
// Clipboard Filter
// ============================================================================

/**
 * Filter options for listing clipboard entries
 */
export interface ClipboardFilter {
  /** Filter by user public key */
  from?: string;
  /** Filter by MIME type (supports wildcards like 'image/*') */
  type?: string;
  /** Filter by entries after this timestamp */
  after?: number;
  /** Filter by entries before this timestamp */
  before?: number;
  /** Filter by team ID */
  team?: string;
  /** Filter by tags */
  tags?: string[];
  /** Maximum number of results */
  limit?: number;
  /** Offset for pagination */
  offset?: number;
}

// ============================================================================
// Paste Options
// ============================================================================

/**
 * Options for paste operation
 */
export interface PasteOptions {
  /** Override MIME type detection */
  type?: ClipboardMimeType;
  /** User-provided label */
  label?: string;
  /** Tags for the entry */
  tags?: string[];
  /** Export policy (default: 'owner') */
  exportPolicy?: ExportPolicy;
  /** View policy (default: 'team') */
  viewPolicy?: ViewPolicy;
  /** Original filename */
  filename?: string;
  /** Source URL */
  sourceUrl?: string;
  /** Device alias override */
  deviceAlias?: string;
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Check if an object is a clipboard entry
 */
export function isClipboardEntry(obj: DAGObject): obj is ClipboardEntry {
  return obj.type === 'clipboard.entry';
}

/**
 * Check if an object is a chunk
 */
export function isChunk(obj: DAGObject): obj is Chunk {
  return obj.type === 'clipboard.chunk';
}

/**
 * Check if an object is a chunk manifest
 */
export function isChunkManifest(obj: DAGObject): obj is ChunkManifest {
  return obj.type === 'clipboard.manifest';
}

/**
 * Check if an object is an export grant
 */
export function isExportGrant(obj: DAGObject): obj is ExportGrant {
  return obj.type === 'clipboard.grant';
}

/**
 * Check if an object is an admin export log
 */
export function isAdminExportLog(obj: DAGObject): obj is AdminExportLog {
  return obj.type === 'clipboard.admin_export';
}
